import { useCallback, useContext, useState } from "react";
import { useEditCandidateProfileMutation } from "../../../../App/Queries/CandidateQueries";
import { IEditCandidateNewPhoneNumber } from "../../../../App/Typings/Candidate";
import { CandidateContext } from "../../../CandidateProfile";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { CandidatePhoneNumberType } from "../../../../App/Enums/CandidateEnums";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";

export const useAddPhoneState = () => {
  const { candidate } = useContext(CandidateContext);
  const { displaySuccess, displayError, displayInfo } = useNotification();
  const { mutate: editProfile, isLoading } = useEditCandidateProfileMutation();
  const [isAddPhone, setIsAddPhone] = useState(false);
  const [newPhone, setNewPhone] = useState<IEditCandidateNewPhoneNumber>({
    phoneNumberRaw: "",
    phoneType: CandidatePhoneNumberType[0],
  });

  const validPhoneNumber = validateNewPhoneNumber(newPhone);
  const emptyPhoneNumber = newPhone.phoneNumberRaw === "";
  const disableSave = isLoading || !validPhoneNumber || emptyPhoneNumber;
  let saveButtonMessage = "Add phone number";
  if (isLoading) {
    saveButtonMessage = "Saving...";
  } else if (!validPhoneNumber) {
    saveButtonMessage = "Invalid phone number";
  } else if (emptyPhoneNumber) {
    saveButtonMessage = "A new phone number must be entered";
  }

  function handleChangePhoneNumber(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setNewPhone((edit) => ({ ...edit, phoneNumberRaw: e.target.value }));
  }
  function handleChangePhoneType(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setNewPhone((edit) => ({
      ...edit,
      phoneType: CandidatePhoneNumberType[Number(e.target.value)],
    }));
  }
  const submitAddPhone = useCallback(() => {
    if (disableSave) {
      handleCancelChanges();
      displayInfo("No changes made to phone number or type.");
      return;
    }
    editProfile(
      {
        candidateId: candidate.id,
        emailAddress: null,
        phoneNumber: null,
        tags: null,
        newPhoneNumber: newPhone,
      },
      {
        onError: () => {
          displayError("Phone number was not added");
        },
        onSettled: (response) => {
          if (response?.success === true) {
            setNewPhone({
              phoneNumberRaw: "",
              phoneType: CandidatePhoneNumberType[0],
            });
            setIsAddPhone(false);
            displaySuccess("Successfully added phone number");
          } else if (response?.errorMessage) {
            displayError(response?.errorMessage);
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPhone.phoneNumberRaw, newPhone.phoneType]);

  const handleCancelChanges = () => {
    setNewPhone({
      phoneNumberRaw: "",
      phoneType: CandidatePhoneNumberType[0],
    });
    setIsAddPhone(false);
  };

  const handleSubmit = useDebouncedHandler(() => {
    submitAddPhone();
  }, 300);

  return {
    getters: {
      isAddPhone,
      newPhone,
      disableSave,
      saveButtonMessage,
      validPhoneNumber,
    },
    handlers: {
      setIsAddPhone,
      handleChangePhoneNumber,
      handleChangePhoneType,
      handleSubmit,
      handleCancelChanges,
    },
  } as const;
};

function validateNewPhoneNumber(newPhoneNumber: IEditCandidateNewPhoneNumber): boolean {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  if (newPhoneNumber.phoneNumberRaw !== "" && !phoneRegex.test(newPhoneNumber.phoneNumberRaw)) {
    return false;
  }
  return true;
}
